import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { SelectField, TextField } from '@roc/ui';
import { BackgroundItemStore } from '../stores/backgroundItemStore';
import { BorrowerInfoCardInternalStore } from 'apps/roc-internal-portal/src/app/modules/borrowerInfoCard/stores/borrowerInfoCardInternalStore';
import { BorrowerBaseballCardStore } from '@roc/feature-borrowers';
import { useMemo } from 'react';

const modalStyles = makeStyles((theme: Theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '10px',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    minWidth: '400px',
  },
}));

const useStyles = makeStyles((theme: Theme) => ({
  fixedWidthText: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  },
  ssnCell: {
    textAlign: 'center',
    minWidth: '120px',
  },
  tableContainer: {
    borderRadius: '10px',
    border: '1px #e0e0e0 solid',
    overflow: 'hidden',
  },
  table: {
    minWidth: 750,
    width: '100%',
    '& td': {
      height: '40px',
      padding: 0,
      paddingTop: '8px',
      paddingBottom: '8px',
      border: '1px solid #e0e0e0',
    },
    '& th:first-child, td[rowSpan], $addNewItemRow td': {
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& table tr:last-child td, & table td[rowSpan]': {
      borderBottom: 'none',
    },
  },

  editableRow: {
    position: 'relative',
    cursor: 'pointer',
    textAlign: 'center',
    '& td': {
      textAlign: 'center'
    },
  },

  headerCell: {
    height: '41px',
    fontWeight: 'bold',
    color: '#757575',
    padding: 0,
    backgroundColor: '#F0FAFF',
    textAlign: 'center',
    border: '1px solid #e0e0e0',
  },

  titleCell: {
    height: '46px',
    padding: 0,
    overflow: 'hidden',
    backgroundColor: '#E2F6FF'
  },

  descriptionCell: {
    whiteSpace: 'pre-wrap',
  },

  inputBase: {
    width: '100%',
    '& input': {
      backgroundColor: '#fdefbf',
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      fontSize: 14,
      color: 'black',
      fontWeight: theme.typography.fontWeightBold,
      padding: '8px 4px',
      cursor: 'pointer',
      '&:focus': {
        boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  autocompletePaper: {
    marginLeft: 0,
    marginRight: 0,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    fontSize: 13,
  },
  autocompleteOption: {
    minHeight: 'auto',
    alignItems: 'flex-start',
    padding: 8,
    '&[data-focus="true"]': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  addNewItemCell: {
    padding: 0,
    height: '40px',
    backgroundColor: '#fdefbf',
  },
  addNewItemRow: {
    '&:hover': {
      backgroundColor: '#fff',
    },
  },
  rowActions: {
    visibility: 'hidden',
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 99,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      visibility: 'visible',
    },
  },
  horizontalScroll: {
    [theme.breakpoints.down('md')]: {
      overflowX: 'scroll',
    },
  },
  clickHereToEdit: {
    color: theme.palette.grey[400],
    fontSize: theme.typography.fontSize * 0.8,
  },
  clickHereToEditHidden: {
    visibility: 'hidden',
  },
  title: {
    color: '#757575',
    fontSize: 20
  },
}));

export const estimatedGUExperienceOptions = [
  { label: 'Experience Score 0', value: 0 },
  { label: 'Experience Score 3 (1-2 Ground Up Construction)', value: 3 },
  { label: 'Experience Score 4 (3-4 Ground Up Construction)', value: 4 },
  { label: 'Experience Score 5 (5+ Ground Up Construction)', value: 5 },
  { label: 'Experienced Developer (20+ Ground Up Construction)', value: 6 },
];

export const estimatedFFExperienceOptions = [
  { label: 'Experience Score 0', value: '' },
  { label: 'Experience Score 1 (No Experience)', value: 1 },
  { label: 'Experience Score 2 (1-2 Investment Properties)', value: 2 },
  { label: 'Experience Score 3 (3-4 Investment Properties)', value: 3 },
  { label: 'Experience Score 4 (5-9 Investment Properties)', value: 4 },
  { label: 'Experience Score 5 (10+ Investment Properties)', value: 5 },
];

export const estimatedRentalExperienceOptions = [
  { label: 'Experience Score 0', value: '' },
  { label: 'Experience Score 1 (1-2 Rentals)', value: 1 },
  { label: 'Experience Score 2 (3-4 Rentals)', value: 2 },
  { label: 'Experience Score 3 (5-9 Rentals)', value: 3 },
  { label: 'Experience Score 4 (10-49 Rentals)', value: 4 },
  { label: 'Experience Score 5 (50+ Rentals)', value: 5 },
];

export const borrowerExpValuesTerm = [
  { label: 'Experience Score 0', value: 0 },
  { label: 'Experience Score 1 (No Experience)', value: 1 },
  { label: 'Experience Score 2 (1-2 Investment Properties)', value: 2 },
  { label: 'Experience Score 3 (3-4 Investment Properties)', value: 3 },
  { label: 'Experience Score 4 (5-9 Investment Properties)', value: 4 },
  { label: 'Experience Score 5 (10+ Investment Properties)', value: 5 },
];

interface BorrowerExperienceDataProps {
  store: BorrowerInfoCardInternalStore | BorrowerBaseballCardStore;
  isInternal?: boolean;
  isExperienceEditable?: boolean;
}

const NewBorrowerExperienceData = observer(({ store, isInternal, isExperienceEditable }: BorrowerExperienceDataProps) => {
  const columns = [
    {
      name: 'Fix & Flip / Stabilized Bridge Experience',
      width: '25%',
    },
    {
      name: 'Ground Up Costruction Experience',
      width: '25%',
    },
    {
      name: 'Term Experience',
      width: '25%',
    },
    {
      name: 'Total # Properties Owned',
      width: '25%',
    },
  ];

  const classes = useStyles();

  const handleBridgeExperienceChange = (value) => {
    store.setExperience(Number(value))
  }

  const handleRentalExperienceChange = (value) => {
    store.setRentalExperience(Number(value))
  }

  const handleRentalMfExperienceChange = (value) => {
    store.setRentalMfExperience(Number(value))
  }

  const handleGroundUpExperienceChange = (value) => {
    store.setGroundUpExperience(Number(value))
  }

  const getExperienceLabel = (options: { label: string; value: any }[], value: any): string => {
    const match = options.find(option => option.value === value);
    return match?.label ?? '';
  };

  const verifiedTrackRecords = store.verifiedTrackRecords;
  const groundUpTrackRecords = store.groundUpTrackRecords;
  const fixAndFlipTrackRecords = store.fixAndFlipTrackRecords;
  const termTrackRecords = store.termTrackRecords;

  if (!store.baseballCardDetails) {
    return <div>Loading...</div>
  }

  return (
    <Box style={{ paddingTop: '15px' }}>
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Typography variant="subtitle2" style={{ color: '#757575', fontWeight: 600 }}>
            Fix and Flip / Stabilized Bridge
          </Typography>
          <Typography variant="body2">
            {getExperienceLabel(estimatedFFExperienceOptions, store.baseballCardDetails?.bridgeExp)}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography variant="subtitle2" style={{ color: '#757575', fontWeight: 600 }}>
            Ground Up Construction
          </Typography>
          <Typography variant="body2">
            {getExperienceLabel(estimatedGUExperienceOptions, store.baseballCardDetails?.groundupExperience)}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography variant="subtitle2" style={{ color: '#757575', fontWeight: 600 }}>
            Term
          </Typography>
          <Typography variant="body2">
            {getExperienceLabel(estimatedRentalExperienceOptions, store.baseballCardDetails?.rentalExp)}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography variant="subtitle2" style={{ color: '#757575', fontWeight: 600 }}>
            Total Properties Verified / Submitted
          </Typography>
          <Typography variant="body2">
            {verifiedTrackRecords.length} / {store?.trackRercords?.length ?? 0}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography variant="subtitle2" style={{ color: '#757575', fontWeight: 600 }}>
            Fix and Flip Verified
          </Typography>
          <Typography variant="body2">
            {fixAndFlipTrackRecords.length}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography variant="subtitle2" style={{ color: '#757575', fontWeight: 600 }}>
            Ground Up Verified
          </Typography>
          <Typography variant="body2">
            {groundUpTrackRecords.length}
          </Typography>
        </Grid>

        <Grid item xs={3}>
          <Typography variant="subtitle2" style={{ color: '#757575', fontWeight: 600 }}>
            Term Verified
          </Typography>
          <Typography variant="body2">
            {termTrackRecords.length}
          </Typography>
        </Grid>

        {isInternal && <Grid item xs={12} style={{ paddingTop: '20px', paddingBottom: '20px' }}>
          <Typography variant="subtitle2" style={{ color: '#757575', fontWeight: 600 }}>
            Comments
          </Typography>
          <Typography variant="body2">
            {store.baseballCardDetails.experienceComment
              ? store.baseballCardDetails.experienceComment
              : ''}
          </Typography>
        </Grid>}
      </Grid>
    </Box>
  );
});

export default NewBorrowerExperienceData;